<template>
  <div>
    <header class="wsp-toolbar">
      <div class="wsp-toolbar__title">
        <span class="wsp-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Divisions &amp; Branches</h1>
          <p>Group physical office locations under each business division</p>
        </div>
      </div>
      <div class="wsp-toolbar__actions">
        <button class="wsp-btn-refresh" :class="{ 'is-loading': loading }" :disabled="loading" @click="fetch">
          <svg class="wsp-btn-refresh__icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ loading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
        <button class="wsp-btn-primary" @click="openCreateDivision">+ Add Division</button>
      </div>
    </header>

    <CAlert v-if="error" color="danger" show>{{ error }}</CAlert>

    <div v-if="loading" class="wsp-loading"><CSpinner size="sm" /> Loading…</div>
    <div v-else-if="!rows.length" class="wsp-empty wsp-card">
      <div class="wsp-empty__title">No divisions yet</div>
      <div>Add your first division to start adding branches.</div>
    </div>
    <div v-else class="ws-divisions">
      <article v-for="d in rows" :key="d.id" class="ws-division">
        <header class="ws-division__head">
          <div class="ws-division__title">
            <span class="ws-division__swatch" :style="{ background: d.color || '#0e5b46' }"></span>
            <div>
              <h3>{{ d.name }} <span style="color:#6b7280;font-weight:500;font-size:0.75rem;">({{ d.short_name }})</span></h3>
              <p v-if="d.description">{{ d.description }}</p>
              <a v-if="d.website" :href="d.website" target="_blank" rel="noopener" style="font-size:0.75rem;color:#0e5b46;">{{ d.website }}</a>
            </div>
          </div>
          <div class="ws-division__actions">
            <button class="wsp-btn-ghost" @click="openCreateBranch(d)">+ Add Branch</button>
            <button class="wsp-icon-btn" @click="openEditDivision(d)" title="Edit">✎</button>
            <button class="wsp-icon-btn wsp-icon-btn--danger" @click="confirmDeleteDivision(d)" title="Delete">🗑</button>
          </div>
        </header>

        <div v-if="!d.branches || !d.branches.length" class="ws-branch-empty">
          No branches yet under {{ d.short_name }}.
        </div>
        <div v-else class="ws-branches">
          <div v-for="b in d.branches" :key="b.id" class="ws-branch">
            <div class="ws-branch__main">
              <div class="ws-branch__name">{{ b.name }}</div>
              <div v-if="b.address" style="font-size:0.78rem;color:#4b5563;">{{ b.address }}</div>
              <div style="display:flex;gap:14px;flex-wrap:wrap;font-size:0.74rem;color:#6b7280;margin-top:4px;">
                <span v-if="b.phone">📞 {{ b.phone }}</span>
                <span v-if="b.head_name">👤 {{ b.head_name }}<span v-if="b.head_role"> · {{ b.head_role }}</span></span>
              </div>
              <div v-if="b.contacts && b.contacts.length" style="margin-top:6px;display:flex;flex-wrap:wrap;gap:4px;">
                <span v-for="(c, i) in b.contacts" :key="i" class="wsp-pill wsp-pill--blue">
                  {{ c.label }}: {{ c.phone }}
                </span>
              </div>
            </div>
            <div class="ws-branch__actions">
              <button class="wsp-icon-btn" @click="openEditBranch(d, b)" title="Edit">✎</button>
              <button class="wsp-icon-btn wsp-icon-btn--danger" @click="confirmDeleteBranch(d, b)" title="Delete">🗑</button>
            </div>
          </div>
        </div>
      </article>
    </div>

    <!-- Division modal -->
    <div v-if="divisionModal" class="wsp-modal-backdrop" @click.self="closeDivisionModal">
      <div class="wsp-modal">
        <header class="wsp-modal__head">
          <h3>{{ divisionForm.id ? 'Edit Division' : 'Add Division' }}</h3>
          <button class="wsp-modal__close" @click="closeDivisionModal">×</button>
        </header>
        <div class="wsp-modal__body">
          <div class="wsp-form-grid">
            <div class="wsp-field">
              <label>Name <span style="color:#b91c1c">*</span></label>
              <input v-model="divisionForm.name" type="text" />
              <span v-if="divisionErrors.name" class="wsp-field__error">{{ divisionErrors.name }}</span>
            </div>
            <div class="wsp-field">
              <label>Short name <span style="color:#b91c1c">*</span></label>
              <input v-model="divisionForm.short_name" type="text" placeholder="MAP" />
              <span v-if="divisionErrors.short_name" class="wsp-field__error">{{ divisionErrors.short_name }}</span>
            </div>
            <div class="wsp-field">
              <label>Color</label>
              <input v-model="divisionForm.color" type="color" />
            </div>
            <div class="wsp-field">
              <label>Position</label>
              <input v-model.number="divisionForm.position" type="number" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Description</label>
              <textarea v-model="divisionForm.description" rows="3"></textarea>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Logo URL</label>
              <input v-model="divisionForm.logo" type="text" placeholder="https://…" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Website URL</label>
              <input v-model="divisionForm.website" type="text" placeholder="https://…" />
            </div>
          </div>
        </div>
        <footer class="wsp-modal__foot">
          <button class="wsp-btn-ghost" :disabled="saving" @click="closeDivisionModal">Cancel</button>
          <button class="wsp-btn-primary" :disabled="saving" @click="saveDivision">
            {{ saving ? 'Saving…' : (divisionForm.id ? 'Save changes' : 'Create division') }}
          </button>
        </footer>
      </div>
    </div>

    <!-- Branch modal -->
    <div v-if="branchModal" class="wsp-modal-backdrop" @click.self="closeBranchModal">
      <div class="wsp-modal wsp-modal--wide">
        <header class="wsp-modal__head">
          <h3>{{ branchForm.id ? 'Edit Branch' : 'Add Branch' }} · {{ branchParent && branchParent.short_name }}</h3>
          <button class="wsp-modal__close" @click="closeBranchModal">×</button>
        </header>
        <div class="wsp-modal__body">
          <div class="wsp-form-grid">
            <div class="wsp-field wsp-field--full">
              <label>Name <span style="color:#b91c1c">*</span></label>
              <input v-model="branchForm.name" type="text" />
              <span v-if="branchErrors.name" class="wsp-field__error">{{ branchErrors.name }}</span>
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Address</label>
              <textarea v-model="branchForm.address" rows="2"></textarea>
            </div>
            <div class="wsp-field">
              <label>Phone</label>
              <input v-model="branchForm.phone" type="text" />
            </div>
            <div class="wsp-field">
              <label>Position</label>
              <input v-model.number="branchForm.position" type="number" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Map embed URL (Google Maps iframe src)</label>
              <input v-model="branchForm.map_embed" type="text" placeholder="https://www.google.com/maps/embed?…" />
            </div>
            <div class="wsp-field">
              <label>Head Name</label>
              <input v-model="branchForm.head_name" type="text" />
            </div>
            <div class="wsp-field">
              <label>Head Role</label>
              <input v-model="branchForm.head_role" type="text" placeholder="Branch Manager" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Head Credentials</label>
              <input v-model="branchForm.head_credentials" type="text" placeholder="MBA, BPharma" />
            </div>
            <div class="wsp-field wsp-field--full">
              <label>Additional Contacts</label>
              <div class="ws-contacts">
                <div v-for="(c, i) in branchForm.contacts" :key="i" class="ws-contact-row">
                  <input v-model="c.label" type="text" placeholder="Label (e.g. Sales)" />
                  <input v-model="c.phone" type="text" placeholder="Phone" />
                  <button class="wsp-icon-btn wsp-icon-btn--danger" @click="removeContact(i)" title="Remove">×</button>
                </div>
                <button class="wsp-btn-ghost" type="button" @click="addContact">+ Add contact</button>
              </div>
            </div>
          </div>
        </div>
        <footer class="wsp-modal__foot">
          <button class="wsp-btn-ghost" :disabled="saving" @click="closeBranchModal">Cancel</button>
          <button class="wsp-btn-primary" :disabled="saving" @click="saveBranch">
            {{ saving ? 'Saving…' : (branchForm.id ? 'Save changes' : 'Create branch') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import websitesApi from '@/api/websites'

const emptyDivision = () => ({ id: null, name: '', short_name: '', color: '#0e5b46', logo: '', description: '', website: '', position: null })
const emptyBranch   = () => ({ id: null, name: '', address: '', phone: '', map_embed: '', head_name: '', head_credentials: '', head_role: '', contacts: [], position: null })

export default {
  name: 'WebsitesDivisions',
  data () {
    return {
      rows: [], loading: false, error: '',
      saving: false,
      divisionModal: false,
      divisionForm: emptyDivision(),
      divisionErrors: {},
      branchModal: false,
      branchParent: null,
      branchForm: emptyBranch(),
      branchErrors: {},
    }
  },
  mounted () { this.fetch() },
  methods: {
    async fetch () {
      this.loading = true; this.error = ''
      try { this.rows = await websitesApi.divisionsList() }
      catch (e) { this.error = this.extractError(e) }
      finally { this.loading = false }
    },

    /* Division */
    openCreateDivision () { this.divisionForm = emptyDivision(); this.divisionErrors = {}; this.divisionModal = true },
    openEditDivision (d) {
      this.divisionForm = {
        id: d.id,
        name: d.name || '',
        short_name: d.short_name || '',
        color: d.color || '#0e5b46',
        logo: d.logo || '',
        description: d.description || '',
        website: d.website || '',
        position: d.position == null ? null : Number(d.position),
      }
      this.divisionErrors = {}; this.divisionModal = true
    },
    closeDivisionModal () { if (!this.saving) this.divisionModal = false },
    async saveDivision () {
      this.divisionErrors = {}
      if (!this.divisionForm.name.trim())       this.divisionErrors.name       = 'Required'
      if (!this.divisionForm.short_name.trim()) this.divisionErrors.short_name = 'Required'
      if (Object.keys(this.divisionErrors).length) return
      this.saving = true
      try {
        const payload = {
          ...this.divisionForm,
          position: this.divisionForm.position == null || this.divisionForm.position === '' ? null : Number(this.divisionForm.position),
        }
        if (this.divisionForm.id) await websitesApi.divisionUpdate(this.divisionForm.id, payload)
        else                       await websitesApi.divisionCreate(payload)
        this.divisionModal = false
        await this.fetch()
      } catch (e) { this.error = this.extractError(e) }
      finally { this.saving = false }
    },
    async confirmDeleteDivision (d) {
      if (!confirm(`Delete "${d.name}" and all of its branches?`)) return
      try { await websitesApi.divisionDelete(d.id); await this.fetch() }
      catch (e) { this.error = this.extractError(e) }
    },

    /* Branch */
    openCreateBranch (parent) {
      this.branchParent = parent
      this.branchForm = emptyBranch()
      this.branchErrors = {}
      this.branchModal = true
    },
    openEditBranch (parent, b) {
      this.branchParent = parent
      this.branchForm = {
        id: b.id,
        name: b.name || '',
        address: b.address || '',
        phone: b.phone || '',
        map_embed: b.map_embed || '',
        head_name: b.head_name || '',
        head_credentials: b.head_credentials || '',
        head_role: b.head_role || '',
        contacts: Array.isArray(b.contacts) ? JSON.parse(JSON.stringify(b.contacts)) : [],
        position: b.position == null ? null : Number(b.position),
      }
      this.branchErrors = {}
      this.branchModal = true
    },
    closeBranchModal () { if (!this.saving) this.branchModal = false },
    addContact () { this.branchForm.contacts.push({ label: '', phone: '' }) },
    removeContact (i) { this.branchForm.contacts.splice(i, 1) },
    async saveBranch () {
      this.branchErrors = {}
      if (!this.branchForm.name.trim()) { this.branchErrors.name = 'Required'; return }
      this.saving = true
      try {
        const payload = {
          ...this.branchForm,
          position: this.branchForm.position == null || this.branchForm.position === '' ? null : Number(this.branchForm.position),
          contacts: (this.branchForm.contacts || []).filter(c => (c.label && c.label.trim()) || (c.phone && c.phone.trim())),
        }
        const divId = this.branchParent.id
        if (this.branchForm.id) await websitesApi.branchUpdate(divId, this.branchForm.id, payload)
        else                    await websitesApi.branchCreate(divId, payload)
        this.branchModal = false
        await this.fetch()
      } catch (e) { this.error = this.extractError(e) }
      finally { this.saving = false }
    },
    async confirmDeleteBranch (d, b) {
      if (!confirm(`Delete branch "${b.name}"?`)) return
      try { await websitesApi.branchDelete(d.id, b.id); await this.fetch() }
      catch (e) { this.error = this.extractError(e) }
    },

    extractError (e) {
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
        || (e && e.message) || 'Request failed'
    },
  },
}
</script>

<style scoped>
.ws-divisions { display: flex; flex-direction: column; gap: 14px; }
.ws-division {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}
.ws-division__head {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
  padding: 14px 18px;
  background: #fbfcfe;
  border-bottom: 1px solid #e5e9f2;
}
.ws-division__title { display: flex; gap: 14px; align-items: flex-start; }
.ws-division__swatch {
  width: 32px; height: 32px; border-radius: 8px;
  flex-shrink: 0;
  border: 1px solid rgba(0,0,0,0.08);
}
.ws-division__title h3 { margin: 0; font-size: 1rem; color: #1f2a44; font-weight: 700; }
.ws-division__title p  { margin: 2px 0 0; font-size: 0.78rem; color: #6b7280; max-width: 600px; }
.ws-division__actions { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }

.ws-branch-empty { padding: 18px; color: #9ca3af; font-size: 0.82rem; text-align: center; }
.ws-branches { display: flex; flex-direction: column; }
.ws-branch {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 12px 18px;
  border-top: 1px solid #f1f3f8;
}
.ws-branch:hover { background: #f9fafb; }
.ws-branch__main { flex: 1; }
.ws-branch__name { font-weight: 600; color: #1f2a44; }
.ws-branch__actions { display: flex; gap: 4px; align-items: center; }

.ws-contacts { display: flex; flex-direction: column; gap: 8px; }
.ws-contact-row {
  display: grid;
  grid-template-columns: 1fr 1fr 36px;
  gap: 8px;
}
</style>
